import React, {
  RefObject,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-python';
import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/ext-language_tools';
import ListParametersTree from '../../listParametersTree';
import VirtualSourceService from '../../../../../domain/VirutalSources/VitualSourceService';
import { Accordion } from '@hydrodigital/design-system';
import { DatePicker, Tag } from 'antd';
import dayjs from 'dayjs';
import InfraestructureContext from '@/contexts/InfraestructureContext';
import { Infrastructure } from '@/domain/Infrastructure/Infrastructure';
import {
  ParameterSource,
  SourceVariable,
} from '@/domain/ParameterSource/ParameterSource';
import { VirtualSource } from '@/domain/VirutalSources/VirtualSource';

const virtualSourceService = new VirtualSourceService();

type Props = {
  infrastructure: Infrastructure;
  source: ParameterSource;
  sources: ParameterSource[];
  onValidationChange?: (error: string | null) => void;
};

type VirtualSourceRefType = {
  getVirtualSourceData: Function;
  getDateStamp: Function;
  validate: () => string | null;
};

function validateVirtualCode(code: string): string | null {
  const pattern = /\$?V\d+\[([^\]]+)\]/g;
  let match;
  while ((match = pattern.exec(code)) !== null) {
    const full = match[0];
    const offset = match[1].trim();
    if (/^[+-]?\d+\.\d+$/.test(offset)) {
      return `Decimal offset not allowed in "${full}". Use integer values only (e.g. [+12], [-5]).`;
    }
    if (/^[+-]?0+$/.test(offset)) {
      return `Zero offset not allowed in "${full}". Use the variable without brackets for the current real-time value.`;
    }
    if (/^\d+$/.test(offset)) {
      return `Positive offset requires an explicit "+" sign in "${full}" — use [+${offset}].`;
    }
    if (!/^[+-]\d+$/.test(offset)) {
      return `Invalid offset format in "${full}". Use [+N] or [-N] where N is a positive integer.`;
    }
  }
  return null;
}

const VirtualSources = forwardRef<VirtualSourceRefType, Props>(
  ({ infrastructure, source, sources, onValidationChange }, ref) => {
    const { value: context, setValue: setContext } = useContext(
      InfraestructureContext,
    );
    const [showAddParam, setShowAddParam] = useState(false);
    const [virtualSource, setVirtualSource] = useState<VirtualSource>({
      sourceId: source.id,
      code: '',
      variables: [],
    });
    const [virtualCurrentTs, setVirtualCurrentTs] = useState<string>(
      source.virtualCurrentTs || dayjs().format('YYYY-MM-DD HH:mm:ss'),
    );
    const [exampleOpen, setExampleOpen] = useState('');
    const [validationError, setValidationError] = useState<string | null>(null);

    useEffect(() => {
      onValidationChange?.(validationError);
    }, [validationError]);
    const aceEditorRef = useRef<any>(null);
    const highlightInitRef = useRef(false);

    // Inject CSS for variable token styling once
    useEffect(() => {
      const styleId = 'ace-variable-highlight-style';
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
          .ace_virtual-var {
            background-color: #E8F9FF !important;
            color: #023E65 !important;
            font-weight: 700 !important;
            border-radius: 3px;
            padding: 1px 2px;
          }
        `;
        document.head.appendChild(style);
      }
    }, []);

    // Add custom highlight rule to Python mode for $V variables
    const setupHighlightRules = useCallback((editor: any) => {
      if (!editor || highlightInitRef.current) return;
      const session = editor.getSession();
      const rules = session.$mode?.$highlightRules?.getRules?.();
      if (!rules?.start) return;

      // Add $V\d+ token at the beginning of rules so it takes priority
      const alreadyAdded = rules.start.some((r: any) => r.token === 'virtual-var');
      if (!alreadyAdded) {
        rules.start.unshift({
          token: 'virtual-var',
          regex: '\\$V\\d+',
        });
      }

      // Force re-tokenize
      session.$mode.$tokenizer = null;
      session.bgTokenizer.setTokenizer(session.$mode.getTokenizer());
      session.bgTokenizer.start(0);
      highlightInitRef.current = true;
    }, []);

    // Custom Ace completer that suggests variables when $ is typed
    const variablesCompleter = useCallback(() => {
      return {
        identifierRegexps: [/\$/],
        getCompletions: (
          _editor: any,
          session: any,
          pos: { row: number; column: number },
          prefix: string,
          callback: Function,
        ) => {
          const line = session.getLine(pos.row);
          const textBeforeCursor = line.substring(0, pos.column);
          const dollarIndex = textBeforeCursor.lastIndexOf('$');

          if (dollarIndex < 0) {
            callback(null, []);
            return;
          }

          const filter = textBeforeCursor.substring(dollarIndex + 1).toLowerCase();
          const completions = virtualSource.variables
            .filter((v) => v.name.toLowerCase().includes('$' + filter) || v.name.toLowerCase().startsWith(filter))
            .map((v) => ({
              caption: v.name,
              value: v.name,
              meta: v.assetName ? `${v.assetName} | ${v.parameterName}` : v.parameterName,
              score: 1000,
            }));

          callback(null, completions);
        },
      };
    }, [virtualSource.variables]);

    // Register ONLY the custom completer (no textCompleter to avoid duplicates)
    useEffect(() => {
      const editor = aceEditorRef.current?.editor;
      if (!editor) return;

      const langTools = (window as any).ace?.require?.('ace/ext/language_tools');
      if (langTools) {
        langTools.setCompleters([variablesCompleter()]);
      } else {
        editor.completers = [variablesCompleter()];
      }
    }, [virtualSource.variables, variablesCompleter]);

    function handleAddVariableParam(param: any) {
      if (
        virtualSource.variables.findIndex((va: SourceVariable) => {
          return va?.parameterId === param?.parameterId;
        }) < 0
      ) {
        setVirtualSource((vs: VirtualSource) => {
          const vsource = {
            ...vs,
            variables: [
              ...vs.variables,
              {
                parameterId: param.id,
                parameterName: param.name,
                assetName: param.infrastructureName,
                name: `$V${vs.variables.length + 1}`,
              } as SourceVariable,
            ] as SourceVariable[],
          };
          setContext({ ...context, selectedVirtualSource: vsource });
          return vsource;
        });
      }
    }

    async function handleGetVirtualSource() {
      const virtualSource = await virtualSourceService.getVirtualSource(source);
      setVirtualSource(virtualSource);
    }

    React.useImperativeHandle(ref, () => ({
      getVirtualSourceData() {
        return virtualSource;
      },
      getDateStamp() {
        return virtualCurrentTs;
      },
      validate() {
        return validateVirtualCode(virtualSource.code);
      },
    }));

    const memoizedId = useMemo(() => source.id, [source.id]);

    useEffect(() => {
      if (source.id) {
        handleGetVirtualSource();
      }
    }, [memoizedId]);

    return (
      <div className="flex column">
        <div>
          {showAddParam ? (
            <ListParametersTree
              locationId={infrastructure?.locationId || 0}
              onSelect={(param: any) => {
                handleAddVariableParam(JSON.parse(param || '{}'));
              }}
            />
          ) : (
            <div className="flex column">
              <div className="flex justify-between mb-2">
                <div className="flex column">
                  <DatePicker
                    showTime
                    onChange={(date, datestring: any) => {
                      setVirtualCurrentTs(
                        dayjs(datestring).format('YYYY-MM-DD HH:mm:ss'),
                      );
                    }}
                    defaultValue={
                      source.virtualCurrentTs
                        ? dayjs(source.virtualCurrentTs)
                        : dayjs()
                    }
                  />
                  <span style={{ color: '#74788D', fontWeight: '500', marginTop: '14px' }}>
                    Code
                  </span>
                </div>
                <span
                  style={{
                    textDecoration: 'underline',
                    color: '#023E65',
                    fontWeight: '500',
                    cursor: 'pointer',
                    textAlign: 'right',
                  }}
                  onClick={() => {
                    setContext({
                      ...context,
                      selectedVirtualSource: virtualSource,
                      showAddVariableVirtualParam: true,
                      handleAddVariableVirtualParam: handleAddVariableParam,
                    });
                  }}
                >
                  Add new variable
                </span>
              </div>
              <span style={{ color: '#74788D', fontWeight: '500', fontSize: '12px', marginBottom: '6px' }}>
                Variables
              </span>
              {virtualSource?.variables.length === 0 && (
                <div
                  style={{
                    color: '#74788D',
                    fontSize: '12px',
                    fontStyle: 'italic',
                    padding: '6px 0',
                    marginBottom: '4px',
                  }}
                >
                  No variables added yet. Click <strong>Add new variable</strong> to include parameters in your formula.
                </div>
              )}
              <div className="flex mb-1" style={{ flexWrap: 'wrap' }}>
                {virtualSource?.variables.slice(0, 4).map((va) => {
                  return (
                   <div
                      key={va.parameterId}
                      className="mr-1 mb-1"
                      style={{
                        display: 'inline-flex',
                        flexDirection: 'column',
                        backgroundColor: '#E8F9FF',
                        border: '1px solid #023E65',
                        borderRadius: '8px',
                        padding: '4px 10px',
                        minWidth: '80px',
                      }}
                    >
                      <span style={{ color: '#023E65', fontWeight: 700, fontSize: '13px', lineHeight: '1.3' }}>
                        {va.name}
                      </span>
                      {va.assetName && (
                        <span style={{ color: '#078CB3', fontWeight: 600, fontSize: '11px', lineHeight: '1.3', marginTop: '2px' }}>
                          {va.assetName}
                        </span>
                      )}
                      <span style={{ color: '#74788D', fontWeight: 400, fontSize: '11px', lineHeight: '1.3', marginTop: '1px' }}>
                        {va.parameterName}
                      </span>
                    </div>
                  );
                })}
              </div>
              {virtualSource?.variables.length > 4 && (
                <div className="flex mb-1">
                  <span
                    style={{
                      textDecoration: 'underline',
                      color: '#44B6DC',
                      fontWeight: '500',
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      setContext({
                        ...context,
                        selectedVirtualSource: virtualSource,
                        showAddVariableVirtualParam: true,
                        handleAddVariableVirtualParam: handleAddVariableParam,
                      });
                    }}
                  >
                    See all
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
        <Accordion
          type="single"
          collapsible
          value={exampleOpen}
          onValueChange={(v) => setExampleOpen(v as string)}
          className="mb-2"
        >
          <Accordion.Item value="examples">
            <Accordion.Trigger
              iconName='info'
              text="Example Code"
            />
            <Accordion.Content>
              <div
                style={{
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  padding: '12px',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                  color: '#333',
                  lineHeight: '1.6',
                }}
              >
                <div style={{ color: '#74788D', marginBottom: '6px', fontFamily: 'sans-serif', fontStyle: 'italic', fontWeight: 600 }}>
                  --- Example 1: Standard Calculation (Current Values) ---
                </div>
                <div style={{ color: '#888', fontFamily: 'sans-serif', marginBottom: '4px' }}>
                  # Goal: Convert Flow from cfs to gpm and add a baseline offset
                </div>
                <div>cfs_to_gpm = 448.831</div>
                <div>offset = 3.85</div>
                <div>x = ($V1 * cfs_to_gpm) + offset</div>
                <div style={{ color: '#74788D', marginTop: '12px', marginBottom: '6px', fontFamily: 'sans-serif', fontStyle: 'italic', fontWeight: 600 }}>
                  --- Example 2: Forecast Calculation (Predictive Analysis) ---
                </div>
                <div style={{ color: '#888', fontFamily: 'sans-serif', marginBottom: '2px' }}>
                  {'# Goal: Calculate the expected change in tank level over the next hour'}
                </div>
                <div style={{ color: '#888', fontFamily: 'sans-serif', marginBottom: '4px' }}>
                  # Assuming a 5-minute sampling rate, [ +12 ] is 60 minutes ahead
                </div>
                <div>current_level = $V2</div>
                <div>forecasted_level = $V2[+12]</div>
                <div>x = forecasted_level - current_level</div>
              </div>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion>
        <AceEditor
          ref={aceEditorRef}
          className="mt-1"
          mode="python"
          theme="github"
          name="editor"
          onChange={(val) => {
            setVirtualSource((vs) => {
              return { ...vs, code: val };
            });
            setValidationError(validateVirtualCode(val));
          }}
          fontSize={14}
          width="100%"
          showPrintMargin={false}
          showGutter={true}
          highlightActiveLine={true}
          value={virtualSource.code}
          setOptions={{
            useWorker: false,
            showLineNumbers: true,
            tabSize: 2,
            enableBasicAutocompletion: true,
            enableLiveAutocompletion: true,
            enableSnippets: false,
          }}
          onLoad={(editor: any) => {
            // Add custom syntax highlighting for $V variables
            setTimeout(() => setupHighlightRules(editor), 50);

          }}
          placeholder="Type $ to insert a variable"
          style={{ maxHeight: '200px' }}
        />
        {validationError && (
          <div
            style={{
              color: '#ff4d4f',
              fontSize: '12px',
              marginTop: '6px',
              padding: '6px 10px',
              backgroundColor: '#fff2f0',
              border: '1px solid #ffccc7',
              borderRadius: '4px',
            }}
          >
            {validationError}
          </div>
        )}
      </div>
    );
  },
);

export default VirtualSources;
